import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import apiClient from '@/lib/api-client'
import { ImageSlotCell } from './ImageSlotCell'

export interface UploadedImage {
  file?: File
  previewUrl: string
  assetUrl: string
  uploading?: boolean
}

interface ImageUploadGridProps {
  images: UploadedImage[]
  onChange: (images: UploadedImage[]) => void
  maxImages?: number
}

interface UploadResponse {
  id: string
  preview_url?: string | null
  s3_url?: string
}

export function ImageUploadGrid({ images, onChange, maxImages = 8 }: ImageUploadGridProps) {
  const { t } = useTranslation()
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const handleUpload = async (file: File, index: number) => {
    const previewUrl = URL.createObjectURL(file)
    const updated = [...images]
    updated[index] = { file, previewUrl, assetUrl: '', uploading: true }
    onChange(updated)

    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await apiClient.post<UploadResponse>('/api/v2/assets/upload', formData, {
        headers: { 'Content-Type': undefined },
      })
      const next = [...updated]
      next[index] = { file, previewUrl, assetUrl: res.data.id ?? '', uploading: false }
      onChange(next)
    } catch {
      const next = [...updated]
      next[index] = { file, previewUrl, assetUrl: '', uploading: false }
      onChange(next)
    }
  }

  const handleFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) return
    handleUpload(file, index)
  }

  const handleRemove = (index: number) => {
    const updated = images.filter((_, i) => i !== index)
    onChange(updated)
  }

  const slots = Array.from({ length: maxImages }, (_, i) => images[i] ?? null)

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
        {t('amazonListing.manual.images')}
      </label>
      <p className="text-xs text-neutral-500 mb-2">{t('amazonListing.manual.uploadHint')}</p>
      <div className="grid grid-cols-4 gap-2">
        {slots.map((img, i) => (
          <ImageSlotCell
            key={i}
            img={img}
            index={i}
            inputRef={(el) => { inputRefs.current[i] = el }}
            onRemove={handleRemove}
            onFileChange={handleFileChange}
          />
        ))}
      </div>
    </div>
  )
}
